import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import {
  translateStream,
  translatePdfStream,
  translateImageStream,
  translateDocStream,
  type LangCode,
  type GenderId,
  type HistoryEntry,
  type ModeId,
  type ModelId,
  type UsageSnapshot,
  type PdfMode,
  type ImageMode,
  type DocMode,
} from '@/lib/baba-api'
import { trackTranslationRun } from '@/lib/analytics'
import {
  coercePdfLayout,
  mediaResultCopyText,
  parseMediaResult,
  parsePartialMediaResult,
  type MediaResult,
} from '@/lib/library/mediaResultContent'
import type { RecentTranslation } from './types'
import { pushRecentTranslation } from './utils'
import { publishUsageSnapshot } from './translationUsage'
import type { TranslationStreamError } from './translationStreamError'
import { formatImageParkingTimeContext } from './imageTimeContext'

type OnStreamError = (err: TranslationStreamError) => void

/** The literal marker the `mode: 'full'` prompts emit between the translation
 * and the key-points summary. The frontend splits the accumulated stream on it:
 * text before = translation, text after = summary. */
const KEY_POINTS_MARKER = '===KEY POINTS==='

/**
 * Split an accumulated `mode: 'full'` stream into the translation body and the
 * key-points summary. While the marker is still streaming in (partial), we keep
 * everything before any partial marker prefix in `translation` so the cursor
 * never flashes the half-typed marker, and leave `summary` empty until the full
 * marker has arrived. If the marker never appears, the whole thing is treated as
 * the translation (graceful — no summary bar).
 */
function splitFullStream(acc: string): { translation: string; summary: string } {
  const idx = acc.indexOf(KEY_POINTS_MARKER)
  if (idx === -1) {
    // Hide a partially-streamed marker prefix at the very end (e.g. "===KEY").
    for (let n = KEY_POINTS_MARKER.length - 1; n > 0; n--) {
      const prefix = KEY_POINTS_MARKER.slice(0, n)
      if (acc.endsWith(prefix)) {
        return { translation: acc.slice(0, acc.length - n), summary: '' }
      }
    }
    return { translation: acc, summary: '' }
  }
  return {
    translation: acc.slice(0, idx),
    summary: acc.slice(idx + KEY_POINTS_MARKER.length).replace(/^\s+/, ''),
  }
}

export type TextTranslationParams = {
  signal: AbortSignal
  text: string
  retryContext?: string
  sourceLang: LangCode
  targetLang: LangCode
  gender: GenderId
  effectiveSpeakingTo: GenderId
  mode: ModeId
  model: ModelId
  style: string
  audienceChangeFromId?: string
  audienceChangeToken?: string
  setOutput: Dispatch<SetStateAction<string>>
  setTransliteration: Dispatch<SetStateAction<string>>
  setUsage: Dispatch<SetStateAction<UsageSnapshot | null>>
  setIsTranslating: (v: boolean) => void
  setRecentTranslations: Dispatch<SetStateAction<RecentTranslation[]>>
  setRetryCount: Dispatch<SetStateAction<number>>
  lastOutputRef: MutableRefObject<string>
  onStreamError: OnStreamError
}

export type TextTranslationResult = {
  historyEntry: HistoryEntry | null
  historySaveFailed: boolean
  /** True when the stream produced a non-empty translation. */
  succeeded: boolean
  /** Present when this translation ran on the "one ledger" gateway path
   *  (see translateGateway/client.ts) — the gateway already recorded this
   *  translation under this id, so the caller should reuse it instead of
   *  pushing a duplicate row onto the shared translations ledger. */
  gatewayHistoryId?: string
  /** Opaque apps/api token that authenticates this result as a continuity baseline. */
  audienceChangeToken?: string
}

/**
 * Coalesces stream chunks into one state update per animation frame. A long
 * translation arrives as hundreds of chunks, and a React update per chunk
 * re-rendered (and re-laid out) the target pane hundreds of times. `flush`
 * applies whatever is pending immediately, for the end of the stream; a hidden
 * tab (no frames) just catches up there.
 */
function frameBatcher(apply: () => void): { schedule: () => void; flush: () => void } {
  let pending: number | null = null
  const hasFrames = typeof requestAnimationFrame === 'function'
  const run = () => {
    pending = null
    apply()
  }
  return {
    schedule: () => {
      if (pending !== null) return
      pending = hasFrames ? requestAnimationFrame(run) : (setTimeout(run, 16) as unknown as number)
    },
    flush: () => {
      if (pending !== null) {
        if (hasFrames) cancelAnimationFrame(pending)
        else clearTimeout(pending)
        pending = null
      }
      apply()
    },
  }
}

export async function runTextTranslation(p: TextTranslationParams): Promise<TextTranslationResult> {
  let acc = ''
  let translitAcc = ''
  let gatewayHistoryId: string | undefined
  let audienceChangeToken: string | undefined
  let completed = false
  /** An error before the stream finished: whatever text arrived is partial. */
  let failed = false
  let outputDirty = false
  let translitDirty = false
  const batch = frameBatcher(() => {
    if (outputDirty) p.setOutput(acc)
    if (translitDirty) p.setTransliteration(translitAcc)
    outputDirty = false
    translitDirty = false
  })
  await translateStream({
    text: p.text,
    sourceLang: p.sourceLang,
    targetLang: p.targetLang,
    gender: p.gender,
    genderContext: p.effectiveSpeakingTo,
    mode: p.mode,
    model: p.model,
    style: p.style || undefined,
    audienceChangeFromId: p.audienceChangeFromId,
    audienceChangeToken: p.audienceChangeToken,
    context: p.retryContext
      ? `Previous translation (provide a different phrasing): ${p.retryContext}`
      : undefined,
    signal: p.signal,
    // The first chunk replaces the previous result wholesale (acc starts
    // empty), so the old sentence stays readable until new text arrives.
    onToken: (chunk) => {
      acc += chunk
      outputDirty = true
      batch.schedule()
    },
    onTransliteration: (t) => {
      translitAcc += t
      translitDirty = true
      batch.schedule()
    },
    onUsage: (u) => publishUsageSnapshot(u, p.setUsage),
    onError: (err) => {
      if (!completed) failed = true
      p.onStreamError(err)
    },
    onDone: (meta) => {
      completed = true
      if (meta?.source === 'gateway' && typeof meta.historyId === 'string') {
        gatewayHistoryId = meta.historyId
      }
      if (typeof meta?.audienceChangeToken === 'string') {
        audienceChangeToken = meta.audienceChangeToken
      }
    },
  })
  batch.flush()

  p.setIsTranslating(false)

  // A stream that failed partway leaves its partial text on screen beside the
  // error, but it is not a translation: it is never pushed to recents, the
  // ledger or progress as if it had finished.
  if (!acc || failed || !completed) {
    return {
      historyEntry: null,
      historySaveFailed: false,
      succeeded: false,
      gatewayHistoryId,
      audienceChangeToken,
    }
  }

  trackTranslationRun({ mode: p.mode, source_lang: p.sourceLang, tab: 'text' })
  p.lastOutputRef.current = acc
  if (!p.retryContext) p.setRetryCount(0)
  p.setRecentTranslations(
    pushRecentTranslation({
      id: `${Date.now()}-${Math.random()}`,
      inputText: p.text,
      output: acc,
      sourceLang: p.sourceLang,
      targetLang: p.targetLang,
      mode: p.mode,
      tab: 'text',
      ts: Date.now(),
    }),
  )

  // Server persists history on POST /translate for signed-in users; no client POST /history.
  return {
    historyEntry: null,
    historySaveFailed: false,
    succeeded: true,
    gatewayHistoryId,
    audienceChangeToken,
  }
}

/**
 * A media translation that ran on the gateway comes back as ONE structured
 * document on the terminal frame, not as prose tokens — see the suppressed
 * `onToken` paths in `lib/translateGateway/mediaClient.ts`. This turns that
 * final payload into either a structured result for the media renderer or,
 * when the model's JSON turned out to be unreadable, plain text for the
 * ordinary prose pane. Returns the line the recents row should show.
 *
 * `serverPages` is the PDF handler's own parse of its own output, which is
 * more forgiving than ours and so is tried first; images pass `undefined`.
 */
function applyGatewayMediaResult(p: {
  translation: string
  serverPages?: unknown
  generatedName?: string | null
  setOutput: Dispatch<SetStateAction<string>>
  setMediaResult: Dispatch<SetStateAction<MediaResult | null>>
}): string {
  const layout = p.serverPages === undefined ? null : coercePdfLayout(p.serverPages, p.generatedName)
  const parsed: MediaResult = layout ?? parseMediaResult(p.translation)
  if (parsed.kind !== 'plain') {
    p.setMediaResult(parsed)
    return mediaResultCopyText(parsed)
  }
  // Readable prose, or JSON this build could not make sense of. Either way the
  // raw text is the only translation that exists, so show it rather than
  // nothing — the same call `parseMediaResult` makes for a history row.
  p.setOutput(parsed.text)
  return parsed.text
}

export type PdfTranslationParams = {
  signal: AbortSignal
  pdfBase64: string
  targetLanguageName: string
  targetLang: LangCode
  pdfMode: PdfMode
  pdfFileName: string | undefined
  pdfPageCount: number | undefined
  gender: GenderId
  setOutput: Dispatch<SetStateAction<string>>
  /** Structured image/PDF documents render through the media result view, not
   *  the prose pane. Null until a gateway translation settles. */
  setMediaResult: Dispatch<SetStateAction<MediaResult | null>>
  setSummary: Dispatch<SetStateAction<string>>
  setUsage: Dispatch<SetStateAction<UsageSnapshot | null>>
  setRecentTranslations: Dispatch<SetStateAction<RecentTranslation[]>>
  onStreamError: OnStreamError
  onStage: (stage: 'uploading' | 'processing' | 'translating' | 'finalizing' | 'done') => void
}

export async function runPdfTranslation(p: PdfTranslationParams): Promise<boolean> {
  let acc = ''
  let started = false
  let succeeded = false
  p.onStage('uploading')
  await translatePdfStream({
    pdfBase64: p.pdfBase64,
    targetLanguage: p.targetLanguageName,
    mode: p.pdfMode,
    filename: p.pdfFileName,
    pageCount: p.pdfPageCount,
    gender: p.gender,
    // Only read by the "one ledger" gateway path, to record the row's
    // direction the way the phone does (see lib/translateGateway/mediaMapping).
    targetLang: p.targetLang,
    signal: p.signal,
    onStatus: p.onStage,
    onToken: (chunk) => {
      p.onStage('translating')
      if (!started) {
        started = true
        p.setOutput('')
        p.setSummary('')
      }
      acc += chunk
      const { translation, summary } = splitFullStream(acc)
      if (summary) p.onStage('finalizing')
      p.setOutput(translation)
      p.setSummary(summary)
    },
    onUsage: (u) => publishUsageSnapshot(u, p.setUsage),
    onError: p.onStreamError,
    onDone: (meta) => {
      p.onStage('done')
      trackTranslationRun({ mode: p.pdfMode, source_lang: 'pdf', tab: 'file' })
      // Gateway: one structured document on the `complete` frame. apps/api
      // fallback: prose already accumulated through onToken.
      const preview =
        meta?.source === 'gateway'
          ? applyGatewayMediaResult({
              translation: typeof meta.translation === 'string' ? meta.translation : '',
              serverPages: meta.pages ?? null,
              generatedName: typeof meta.generatedName === 'string' ? meta.generatedName : null,
              setOutput: p.setOutput,
              setMediaResult: p.setMediaResult,
            })
          : acc
            ? splitFullStream(acc).translation || acc
            : ''
      if (preview) {
        succeeded = true
        p.setRecentTranslations(
          pushRecentTranslation({
            id: `${Date.now()}-${Math.random()}`,
            inputText: p.pdfFileName || 'PDF',
            output: preview,
            sourceLang: 'pdf' as LangCode,
            targetLang: p.targetLang,
            mode: p.pdfMode as unknown as ModeId,
            tab: 'file',
            ts: Date.now(),
          }),
        )
      }
    },
  })
  return succeeded
}

export type ImageTranslationParams = {
  signal: AbortSignal
  imageBase64: string
  targetLanguageName: string
  targetLang: LangCode
  imageModePick: ImageMode
  imageFileName: string | undefined
  gender: GenderId
  setOutput: Dispatch<SetStateAction<string>>
  /** See PdfTranslationParams. */
  setMediaResult: Dispatch<SetStateAction<MediaResult | null>>
  setSummary: Dispatch<SetStateAction<string>>
  setOriginalText: Dispatch<SetStateAction<string>>
  setUsage: Dispatch<SetStateAction<UsageSnapshot | null>>
  setRecentTranslations: Dispatch<SetStateAction<RecentTranslation[]>>
  onStreamError: OnStreamError
  onStage: (stage: 'uploading' | 'extracting' | 'translating' | 'finalizing' | 'done') => void
}

export async function runImageTranslation(p: ImageTranslationParams): Promise<boolean> {
  let acc = ''
  let firstImageToken = true
  let succeeded = false
  /** Set once `onDone` has run: a partial landing after it must never paint
   *  over the finished card. */
  let finished = false
  /** The newest readable partial card, waiting for the next frame. */
  let pendingPartial: MediaResult | null = null
  /** The partial currently on screen, so an unfinished stream can take back
   *  exactly what it put there and nothing else. */
  let shownPartial: MediaResult | null = null
  // Partials arrive ~5/s; one paint per animation frame at most, and none
  // after completion. Partials are display only: no recents, analytics,
  // usage or ledger side effect ever runs from them.
  const partialBatch = frameBatcher(() => {
    if (finished || !pendingPartial) return
    shownPartial = pendingPartial
    pendingPartial = null
    p.setMediaResult(shownPartial)
  })
  p.onStage('uploading')
  await translateImageStream({
    imageBase64: p.imageBase64,
    language: p.targetLanguageName,
    mode: p.imageModePick,
    model: 'deep',
    detailLevel: 'high',
    gender: p.gender,
    timeContext: formatImageParkingTimeContext(),
    // See the PDF call above.
    targetLang: p.targetLang,
    signal: p.signal,
    onStatus: (status) => {
      p.onStage(status)
    },
    onExtracted: (extractedHebrew) => {
      // The OCR'd Hebrew is the original-language pane content.
      p.setOriginalText(extractedHebrew)
    },
    onPartialStructured: (partial) => {
      if (finished) return
      const card = parsePartialMediaResult(partial)
      if (!card) return
      pendingPartial = card
      partialBatch.schedule()
    },
    onToken: (chunk) => {
      if (firstImageToken) {
        firstImageToken = false
        p.setOutput('')
        p.setSummary('')
      }
      acc += chunk
      const { translation, summary } = splitFullStream(acc)
      if (summary) p.onStage('finalizing')
      p.setOutput(translation)
      p.setSummary(summary)
    },
    onUsage: (u) => publishUsageSnapshot(u, p.setUsage),
    onError: p.onStreamError,
    onDone: (meta) => {
      finished = true
      pendingPartial = null
      // The final document replaces the partial wholesale. Cleared first so a
      // final that turns out to be prose (rendered from `output`) is not
      // hidden behind the half-written card; React batches both updates.
      if (shownPartial) {
        p.setMediaResult(null)
        shownPartial = null
      }
      p.onStage('done')
      trackTranslationRun({ mode: p.imageModePick, source_lang: 'image', tab: 'file' })
      const preview =
        meta?.source === 'gateway'
          ? applyGatewayMediaResult({
              translation: typeof meta.translation === 'string' ? meta.translation : '',
              setOutput: p.setOutput,
              setMediaResult: p.setMediaResult,
            })
          : acc
            ? splitFullStream(acc).translation || acc
            : ''
      if (preview) {
        succeeded = true
        p.setRecentTranslations(
          pushRecentTranslation({
            id: `${Date.now()}-${Math.random()}`,
            inputText: p.imageFileName || 'Image',
            output: preview,
            sourceLang: 'image' as LangCode,
            targetLang: p.targetLang,
            mode: p.imageModePick as unknown as ModeId,
            tab: 'file',
            ts: Date.now(),
          }),
        )
      }
    },
  })
  finished = true
  pendingPartial = null
  // A stream that ended without a finished card (cancelled, failed, or done
  // with nothing readable) must not leave its half-written card behind as if
  // it were a result: Copy, auto-copy and Share would treat it as final. Only
  // the partial this run painted is removed, never a newer run's result.
  if (!succeeded && shownPartial) {
    const stale = shownPartial
    p.setMediaResult((current) => (current === stale ? null : current))
  }
  return succeeded
}

export type DocTranslationParams = {
  signal: AbortSignal
  docBase64: string
  docFileType: 'docx' | 'pptx'
  docFileName: string | undefined
  targetLanguageName: string
  targetLang: LangCode
  docMode: DocMode
  gender: GenderId
  setOutput: Dispatch<SetStateAction<string>>
  setSummary: Dispatch<SetStateAction<string>>
  setUsage: Dispatch<SetStateAction<UsageSnapshot | null>>
  setRecentTranslations: Dispatch<SetStateAction<RecentTranslation[]>>
  onStreamError: OnStreamError
  onStage: (stage: 'uploading' | 'processing' | 'translating' | 'finalizing' | 'done') => void
}

export async function runDocTranslation(p: DocTranslationParams): Promise<boolean> {
  let acc = ''
  let started = false
  let succeeded = false
  p.onStage('uploading')
  await translateDocStream({
    fileBase64: p.docBase64,
    fileType: p.docFileType,
    filename: p.docFileName,
    targetLanguage: p.targetLanguageName,
    mode: p.docMode,
    gender: p.gender,
    signal: p.signal,
    onStatus: p.onStage,
    onToken: (chunk) => {
      p.onStage('translating')
      if (!started) {
        started = true
        p.setOutput('')
        p.setSummary('')
      }
      acc += chunk
      const { translation, summary } = splitFullStream(acc)
      if (summary) p.onStage('finalizing')
      p.setOutput(translation)
      p.setSummary(summary)
    },
    onUsage: (u) => publishUsageSnapshot(u, p.setUsage),
    onError: p.onStreamError,
    onDone: () => {
      p.onStage('done')
      trackTranslationRun({ mode: p.docMode, source_lang: 'doc', tab: 'file' })
      if (acc) {
        succeeded = true
        const { translation } = splitFullStream(acc)
        p.setRecentTranslations(
          pushRecentTranslation({
            id: `${Date.now()}-${Math.random()}`,
            inputText: p.docFileName || 'Document',
            output: translation || acc,
            sourceLang: 'doc' as LangCode,
            targetLang: p.targetLang,
            mode: p.docMode as unknown as ModeId,
            tab: 'file',
            ts: Date.now(),
          }),
        )
      }
    },
  })
  return succeeded
}
